import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, useQuery } from "react-query";
import api from "../services/api";
import "./ApplicationForm.css";

interface Candidate {
  id: string | number;
  first_name?: string;
  last_name?: string;
  email?: string;
  main_skill?: string;
  seniority?: string;
  location?: string;
  expected_rate?: string | number;
}

interface Job {
  id: number;
  title: string;
  department?: string;
  location?: string;
  employment_type?: string;
  seniority?: string;
  skills?: string[];
  description?: string;
  status?: string;
}

interface ApplicationFormProps {
  candidate?: Candidate;
  jobId?: string | number;
  onSave: () => void;
  onCancel: () => void;
}

interface FormData {
  candidate_id: string;
  job_id: string;
  status: string;
  expected_rate: string;
  notes: string;
}

interface FormErrors {
  general?: string;
  candidate_id?: string;
  job_id?: string;
  expected_rate?: string;
  [key: string]: string | undefined;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({
  candidate,
  jobId,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<FormData>({
    candidate_id: "",
    job_id: jobId ? String(jobId) : "",
    status: "applied",
    expected_rate: "",
    notes: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Fetch available jobs
  const { data: jobsData, isLoading: jobsLoading } = useQuery<{ data?: Job[] }>(
    "jobs",
    () => api.recruiting.getJobs() as Promise<{ data?: Job[] }>,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  // Fetch candidates when jobId is provided but candidate is not
  const { data: candidatesData, isLoading: candidatesLoading } = useQuery<{
    data?: Candidate[];
  }>(
    "candidates",
    () =>
      api.recruiting.getCandidates({ limit: 100 }) as Promise<{
        data?: Candidate[];
      }>,
    {
      enabled: !!jobId && !candidate, // Only fetch when we need candidate selection
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  // Initialize form data when candidate or jobId prop changes
  useEffect(() => {
    if (candidate) {
      setFormData((prev) => ({
        ...prev,
        candidate_id: String(candidate.id),
        expected_rate: String(candidate.expected_rate || ""),
      }));
    }
  }, [candidate]);

  // Initialize job_id when jobId prop is provided
  useEffect(() => {
    if (jobId) {
      setFormData((prev) => ({
        ...prev,
        job_id: String(jobId),
      }));
    }
  }, [jobId]);

  // Create application mutation
  const createApplicationMutation = useMutation(
    async (data: Record<string, unknown>) => {
      return await api.recruiting.createApplication(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("applications");
        if (candidate?.id) {
          queryClient.invalidateQueries([
            "candidate-applications",
            candidate.id,
          ]);
        }
        if (jobId) {
          queryClient.invalidateQueries(["job-applications", jobId]);
        }
        onSave();
      },
      onError: (error: any) => {
        console.error("Error creating application:", error);
        const code = error?.response?.data?.code;
        if (code === "DUPLICATE_APPLICATION") {
          setErrors({
            general: t("applications.form.validation.duplicateApplication"),
          });
        } else {
          setErrors({
            general: t("applications.form.validation.createFailed"),
          });
        }
      },
    },
  );

  const jobs = jobsData?.data || [];
  const candidates = candidatesData?.data || [];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.candidate_id) {
      newErrors.candidate_id = t(
        "applications.form.validation.candidateRequired",
      );
    }

    if (!formData.job_id) {
      newErrors.job_id = t("applications.form.validation.jobRequired");
    }

    if (
      formData.expected_rate &&
      Number.isNaN(Number.parseFloat(formData.expected_rate))
    ) {
      newErrors.expected_rate = t(
        "applications.form.validation.expectedRateNumber",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare data for API
    const submitData = {
      ...formData,
      expected_rate: formData.expected_rate
        ? Number.parseFloat(formData.expected_rate)
        : null,
    };

    createApplicationMutation.mutate(submitData);
  };

  if (jobsLoading || (jobId && !candidate && candidatesLoading)) {
    return (
      <div className="application-form">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t("applications.form.loading.formData")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="application-form">
      <div className="form-header">
        <h2>{t("applications.form.title")}</h2>
        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
          >
            {t("common:actions.cancel")}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="form-content">
        {errors.general && (
          <div className="error-message general-error">{errors.general}</div>
        )}

        {/* Candidate Information - Show candidate card when candidate prop is provided */}
        {candidate && (
          <div className="form-section">
            <h3>{t("applications.form.sections.candidate")}</h3>
            <div className="candidate-info">
              <div className="candidate-card">
                <div className="candidate-header">
                  <h4>
                    {candidate.first_name} {candidate.last_name}
                  </h4>
                  <span className="candidate-email">{candidate.email}</span>
                </div>
                <div className="candidate-details">
                  <div className="detail-row">
                    <span className="label">
                      {t("applications.form.labels.mainSkill")}
                    </span>
                    <span className="value">
                      {candidate.main_skill ||
                        t("applications.form.labels.notSpecified")}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">
                      {t("applications.form.labels.seniority")}
                    </span>
                    <span className="value">
                      {candidate.seniority ||
                        t("applications.form.labels.notSpecified")}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">
                      {t("applications.form.labels.location")}
                    </span>
                    <span className="value">
                      {candidate.location ||
                        t("applications.form.labels.notSpecified")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Candidate Selection - Show when jobId is provided but candidate is not */}
        {!candidate && jobId && (
          <div className="form-section">
            <h3>{t("applications.form.sections.candidateSelection")}</h3>
            <div className="form-group">
              <label htmlFor="candidate_id">
                {t("applications.form.fields.selectCandidate")} *
              </label>
              <select
                id="candidate_id"
                name="candidate_id"
                value={formData.candidate_id}
                onChange={(e) => {
                  // Update expected_rate when candidate is selected
                  const selectedCandidate = candidates.find(
                    (c) => c.id === Number.parseInt(e.target.value, 10),
                  );
                  if (selectedCandidate?.expected_rate) {
                    setFormData((prev) => ({
                      ...prev,
                      candidate_id: e.target.value,
                      expected_rate:
                        String(selectedCandidate.expected_rate) ||
                        prev.expected_rate,
                    }));
                  } else {
                    handleInputChange(e);
                  }
                }}
                className={errors.candidate_id ? "error" : ""}
                required
              >
                <option value="">
                  {t("applications.form.placeholders.chooseCandidate")}
                </option>
                {candidates.map((cand) => (
                  <option key={cand.id} value={cand.id}>
                    {cand.first_name} {cand.last_name} - {cand.email}{" "}
                    {cand.main_skill ? `(${cand.main_skill})` : ""}
                  </option>
                ))}
              </select>
              {errors.candidate_id && (
                <span className="error-text">{errors.candidate_id}</span>
              )}
            </div>

            {formData.candidate_id && (
              <div className="selected-candidate-info">
                {(() => {
                  const selectedCandidate = candidates.find(
                    (c) => c.id === Number.parseInt(formData.candidate_id, 10),
                  );
                  return selectedCandidate ? (
                    <div className="candidate-card">
                      <div className="candidate-header">
                        <h4>
                          {selectedCandidate.first_name}{" "}
                          {selectedCandidate.last_name}
                        </h4>
                        <span className="candidate-email">
                          {selectedCandidate.email}
                        </span>
                      </div>
                      <div className="candidate-details">
                        <div className="detail-row">
                          <span className="label">
                            {t("applications.form.labels.mainSkill")}
                          </span>
                          <span className="value">
                            {selectedCandidate.main_skill ||
                              t("applications.form.labels.notSpecified")}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="label">
                            {t("applications.form.labels.seniority")}
                          </span>
                          <span className="value">
                            {selectedCandidate.seniority ||
                              t("applications.form.labels.notSpecified")}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="label">
                            {t("applications.form.labels.location")}
                          </span>
                          <span className="value">
                            {selectedCandidate.location ||
                              t("applications.form.labels.notSpecified")}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            )}
          </div>
        )}

        {/* Job Selection */}
        <div className="form-section">
          <h3>{t("applications.form.sections.jobSelection")}</h3>
          {jobId ? (
            // When jobId is provided, show read-only job info
            <div className="form-group">
              <span className="form-label-text">
                {t("applications.form.fields.selectJob")}
              </span>
              <section
                className="selected-job-info"
                aria-label="Selected job information"
              >
                {(() => {
                  const selectedJob = jobs.find(
                    (job) => job.id === Number.parseInt(String(jobId), 10),
                  );
                  return selectedJob ? (
                    <div className="job-card">
                      <h4>{selectedJob.title}</h4>
                      <div className="job-details">
                        <div className="detail-row">
                          <span className="label">
                            {t("applications.form.labels.department")}
                          </span>
                          <span className="value">
                            {selectedJob.department ||
                              t("applications.form.labels.notSpecified")}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="label">
                            {t("applications.form.labels.location")}
                          </span>
                          <span className="value">
                            {selectedJob.location ||
                              t("applications.form.labels.notSpecified")}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="label">
                            {t("applications.form.labels.employmentType")}
                          </span>
                          <span className="value">
                            {selectedJob.employment_type ||
                              t("applications.form.labels.notSpecified")}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="label">
                            {t("applications.form.labels.seniority")}
                          </span>
                          <span className="value">
                            {selectedJob.seniority ||
                              t("applications.form.labels.notSpecified")}
                          </span>
                        </div>
                        {selectedJob.skills &&
                          selectedJob.skills.length > 0 && (
                            <div className="detail-row">
                              <span className="label">
                                {t("applications.form.labels.skills")}
                              </span>
                              <span className="value">
                                {selectedJob.skills.join(", ")}
                              </span>
                            </div>
                          )}
                      </div>
                      {selectedJob.description && (
                        <div className="job-description">
                          <h5>{t("applications.form.labels.description")}</h5>
                          <p>{selectedJob.description}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="loading-state">
                      <p>{t("applications.form.loading.jobInfo")}</p>
                    </div>
                  );
                })()}
              </section>
            </div>
          ) : (
            // When jobId is not provided, show job selection dropdown
            <div className="form-group">
              <label htmlFor="job_id">
                {t("applications.form.fields.selectJob")} *
              </label>
              <select
                id="job_id"
                name="job_id"
                value={formData.job_id}
                onChange={handleInputChange}
                className={errors.job_id ? "error" : ""}
                required
              >
                <option value="">
                  {t("applications.form.placeholders.chooseJob")}
                </option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.title} - {job.location} ({job.status})
                  </option>
                ))}
              </select>
              {errors.job_id && (
                <span className="error-text">{errors.job_id}</span>
              )}
            </div>
          )}

          {/* Show job details when job is selected from dropdown (not when jobId prop is provided) */}
          {!jobId && formData.job_id && (
            <div className="selected-job-info">
              {(() => {
                const selectedJob = jobs.find(
                  (job) => job.id === Number.parseInt(formData.job_id, 10),
                );
                return selectedJob ? (
                  <div className="job-card">
                    <h4>{selectedJob.title}</h4>
                    <div className="job-details">
                      <div className="detail-row">
                        <span className="label">
                          {t("applications.form.labels.department")}
                        </span>
                        <span className="value">
                          {selectedJob.department ||
                            t("applications.form.labels.notSpecified")}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="label">
                          {t("applications.form.labels.location")}
                        </span>
                        <span className="value">
                          {selectedJob.location ||
                            t("applications.form.labels.notSpecified")}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="label">
                          {t("applications.form.labels.employmentType")}
                        </span>
                        <span className="value">
                          {selectedJob.employment_type ||
                            t("applications.form.labels.notSpecified")}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="label">
                          {t("applications.form.labels.seniority")}
                        </span>
                        <span className="value">
                          {selectedJob.seniority ||
                            t("applications.form.labels.notSpecified")}
                        </span>
                      </div>
                      {selectedJob.skills && selectedJob.skills.length > 0 && (
                        <div className="detail-row">
                          <span className="label">
                            {t("applications.form.labels.skills")}
                          </span>
                          <span className="value">
                            {selectedJob.skills.join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                    {selectedJob.description && (
                      <div className="job-description">
                        <h5>{t("applications.form.labels.description")}</h5>
                        <p>{selectedJob.description}</p>
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>

        {/* Application Details */}
        <div className="form-section">
          <h3>{t("applications.form.sections.applicationDetails")}</h3>
          <div className="form-row">
            <div className="form-group">
              <span className="form-label-text">
                {t("applications.form.fields.status")}
              </span>
              <span
                className="pipeline-status-badge status-applied"
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  borderRadius: "12px",
                  backgroundColor: "#f5f3ff",
                  color: "#8b5cf6",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              >
                Applied
              </span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="expected_rate">
              {t("applications.form.fields.expectedRate")}
            </label>
            <input
              type="number"
              id="expected_rate"
              name="expected_rate"
              value={formData.expected_rate}
              onChange={handleInputChange}
              className={errors.expected_rate ? "error" : ""}
              min="0"
              step="0.01"
              placeholder={t("applications.form.placeholders.expectedRate")}
            />
            {errors.expected_rate && (
              <span className="error-text">{errors.expected_rate}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="notes">
              {t("applications.form.fields.initialNotes")}
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder={t("applications.form.placeholders.notes")}
            />
          </div>
        </div>

        <div className="form-footer">
          <button
            type="submit"
            disabled={createApplicationMutation.isLoading}
            className="btn-primary"
          >
            {createApplicationMutation.isLoading
              ? t("applications.form.buttons.creating")
              : t("applications.form.buttons.create")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApplicationForm;
